import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarOff,
  ClipboardCheck, Search, CheckCircle2, FileDown,
} from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button, Card, CardBody, Chip, Input, Skeleton,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, Stat, StatGroup, StatusPill } from '@vnetwork/ui/data';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAdminDayOffs, useAdminDayOffStats, type AdminDayOff } from '@/api/dayoffs-admin';
import { dataCentralApi } from '@/api/client';
import { paginationView } from '@/pages/customers/pagination-summary';
import { formatDate } from '@/lib/format';
import { statusTone } from '@/pages/account/components/status-tone';
import type { ColumnDef } from '@vnetwork/ui/data';

const PAGE_SIZE = 20;

const typeLabel = (s: string) =>
  s ? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '—';

function rangeDisplay(d: AdminDayOff): string {
  const dates = (d.day_off_range_time ?? [])
    .map((r) => r.start_day_off || r.startdayoff || '')
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return '—';
  const first = formatDate(dates[0]);
  const last = formatDate(dates[dates.length - 1]);
  return first === last ? first : `${first} → ${last}`;
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [d, setD] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setD(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return d;
}

function errMsg(err: unknown): string {
  const r = (err as { response?: { data?: { error_description?: string; message?: string } } })?.response;
  return r?.data?.error_description ?? r?.data?.message ?? 'Something went wrong.';
}

/** HRM stats bar — 7 cards matching the legacy StatsHRM widget. */
function DayoffStatsBar() {
  const { data: stats, isLoading } = useAdminDayOffStats();

  if (isLoading) {
    return (
      <StatGroup columns={7}>
        {Array.from({ length: 7 }).map((_, i) => (
          <Card key={i} flat><CardBody><Skeleton style={{ height: 48 }} /></CardBody></Card>
        ))}
      </StatGroup>
    );
  }

  const items: Array<{ label: string; count: number; days: number; dotColor: string }> = [
    { label: 'Pending', count: stats?.pending_count ?? 0, days: stats?.pending_days ?? 0, dotColor: 'var(--vn-warning-solid)' },
    { label: 'Leader approved', count: stats?.leader_approved_count ?? 0, days: stats?.leader_approved_days ?? 0, dotColor: 'var(--vn-info-solid)' },
    { label: 'Approved', count: stats?.approved_count ?? 0, days: stats?.approved_days ?? 0, dotColor: 'var(--vn-success-solid)' },
    { label: 'Rejected', count: stats?.rejected_count ?? 0, days: stats?.rejected_days ?? 0, dotColor: 'var(--vn-danger-solid)' },
    { label: 'Cancelled', count: stats?.cancelled_count ?? 0, days: stats?.cancelled_days ?? 0, dotColor: 'var(--vn-fg-subtle)' },
  ];

  return (
    <StatGroup columns={7}>
      <Card flat>
        <CardBody>
          <Stat label="Total requests" value={stats?.total_requests ?? 0} />
        </CardBody>
      </Card>
      <Card flat>
        <CardBody>
          <Stat label="Total days" value={stats?.total_days ?? 0} />
        </CardBody>
      </Card>
      {items.map((item) => (
        <Card key={item.label} flat>
          <CardBody>
            <Stat
              dotColor={item.dotColor}
              label={item.label}
              value={item.count}
              hint={`${item.days} days`}
            />
          </CardBody>
        </Card>
      ))}
    </StatGroup>
  );
}

/** Admin day-offs list — all employees, server-paginated, bulk approve. */
export default function DayoffsAdminPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [approving, setApproving] = useState(false);

  const { data, isLoading, isError, isFetching, refetch } = useAdminDayOffs({
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
  });

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? rows.length;
  const pageCount = pagination?.total_pages ?? 1;

  const selectedRows = useMemo(
    () => rows.filter((r) => selection[String(r.day_off_id)]),
    [rows, selection],
  );

  // Approvable rows: pending or leader-approved
  const approvableSelected = useMemo(
    () => selectedRows.filter((r) => r.day_off_status === 'pending' || r.day_off_status === 'leader-approved'),
    [selectedRows],
  );

  function onSearch(v: string) {
    setRawQuery(v);
    setPage(1);
  }

  /** BOD approve-all selected */
  async function handleBulkBodApprove() {
    if (!approvableSelected.length) return;
    setApproving(true);
    try {
      const results = await Promise.allSettled(
        approvableSelected.map((r) =>
          dataCentralApi.put(`/employees/day_offs/${r.day_off_id}/bod/approve`, { day_off_feedback_bod: '' }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) toast.error(`${failed} of ${approvableSelected.length} could not be BOD-approved.`);
      else toast.success(`${approvableSelected.length} day-off(s) BOD-approved`);
      setSelection({});
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setApproving(false);
    }
  }

  /** Leader approve-all selected */
  async function handleBulkLeaderApprove() {
    if (!approvableSelected.length) return;
    setApproving(true);
    try {
      const results = await Promise.allSettled(
        approvableSelected.map((r) =>
          dataCentralApi.put(`/employees/day_offs/${r.day_off_id}/lead/approve`, { day_off_feedback_lead: '' }),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) toast.error(`${failed} of ${approvableSelected.length} could not be leader-approved.`);
      else toast.success(`${approvableSelected.length} day-off(s) leader-approved`);
      setSelection({});
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setApproving(false);
    }
  }

  const columns = useMemo<ColumnDef<AdminDayOff, unknown>[]>(
    () => [
      {
        id: 'id',
        header: 'ID',
        cell: ({ row }) => (
          <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
            #{row.original.day_off_id}
          </span>
        ),
        size: 72,
      },
      {
        id: 'employee',
        header: 'Employee',
        cell: ({ row }) => (
          <div style={{ display: 'grid', gap: 'var(--vn-space-0)' }}>
            <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.employee_name}</span>
            <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-xs)' }}>
              {row.original.department_name}
            </span>
          </div>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) => typeLabel(row.original.day_off_type),
        size: 120,
      },
      {
        id: 'dates',
        header: 'Dates',
        cell: ({ row }) => rangeDisplay(row.original),
        size: 180,
      },
      {
        id: 'days',
        header: 'Days',
        cell: ({ row }) => String(row.original.number_of_day_off ?? 0),
        size: 72,
      },
      {
        id: 'reason',
        header: 'Reason',
        cell: ({ row }) => (
          <span
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: 'var(--vn-fg-muted)',
              fontSize: 'var(--vn-font-size-sm)',
            }}
          >
            {row.original.day_off_reason || '—'}
          </span>
        ),
      },
      {
        id: 'approvals',
        header: 'Approvals',
        cell: ({ row }) => (
          <div style={{ display: 'inline-flex', gap: 'var(--vn-space-1)' }}>
            <Chip size="sm" tone={row.original.day_off_leader_confirmed ? 'success' : 'neutral'}>
              Leader
            </Chip>
            <Chip size="sm" tone={row.original.day_off_bod_confirmed ? 'success' : 'neutral'}>
              BOD
            </Chip>
          </div>
        ),
        size: 140,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={statusTone(row.original.day_off_status)}>
            {row.original.day_off_status || '—'}
          </StatusPill>
        ),
        size: 140,
      },
      {
        id: 'created',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.day_off_created_at),
        size: 110,
      },
    ],
    [],
  );

  const pv = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);

  return (
    <Container size="full">
      <PageHeader
        title="Day-offs"
        description="All employee leave requests — approve, reject, and track statistics."
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <DayoffStatsBar />

        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <Input
              value={rawQuery}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="Search by name, department, type, reason…"
              startSlot={<Search size={15} />}
              style={{ maxWidth: 320 }}
            />
          }
          end={
            selectedRows.length > 0 ? (
              <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  {selectedRows.length} selected
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="primary" startIcon={<ClipboardCheck size={15} />} disabled={approving || approvableSelected.length === 0}>
                      Approve ({approvableSelected.length})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleBulkLeaderApprove} disabled={approving}>
                      <CheckCircle2 size={14} /> Leader approve all
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleBulkBodApprove} disabled={approving}>
                      <CheckCircle2 size={14} /> BOD approve all
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                startIcon={<FileDown size={15} />}
                onClick={() => {
                  const params = new URLSearchParams({ page_size: '9999' });
                  if (query) params.set('q', query);
                  window.open(
                    `/api/v4/employees/day_offs/list/export?${params}`,
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
              >
                Export
              </Button>
            )
          }
        />

        {isError ? (
          <Alert
            tone="danger"
            title="Failed to load day-offs"
            actions={
              <Button size="sm" variant="ghost" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        ) : isLoading ? (
          <Card flat>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 24 }} />
              ))}
            </CardBody>
          </Card>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(r) => String(r.day_off_id)}
            enableSelection
            rowSelection={selection}
            onRowSelectionChange={setSelection}
            onRowClick={(r) => navigate(`/dayoffs/${r.day_off_id}`)}
            emptyState={
              <EmptyState
                icon={<CalendarOff size={24} />}
                title="No day-off requests"
                description="Employee leave requests will appear here."
              />
            }
            footer={
              <Pagination
                page={page}
                pageCount={pv.pageCount}
                onPageChange={setPage}
                summary={`${pv.summary}${isFetching ? ' …' : ''}`}
              />
            }
          />
        )}
      </div>
    </Container>
  );
}
